/**
 * The tools that sit in the dock's tab bar (spec §B3): a toolbar group, its
 * separator, a square icon button, a segmented radiogroup and the ◀ label ▶
 * stepper. Every one is `h-7 text-xs` (`DOCK_CONTROL`), so a row of them lines
 * up without anybody typing a height.
 *
 * Two kinds of "cannot do that right now", answered differently everywhere in
 * this file, as CLAUDE.md requires:
 *  - a DATA condition (`disabledReason`): nothing to step to, no walk on this
 *    alignment. `aria-disabled`, muted, inert, the reason in `title`.
 *  - a PAYWALL (`gate` with `allowed: false`): muted and `aria-disabled` too,
 *    but the click still goes somewhere — `gate.promptUpgrade()` — and an
 *    `UpgradeBadge` says which plan.
 * Neither ever sets the HTML `disabled` attribute: it would take the control
 * out of hover and the tab order, and on a gated control it would swallow the
 * one click that reaches the upgrade dialog.
 */
import { useRef, type ComponentProps, type KeyboardEvent, type ReactNode } from 'react';

import { Crosshair } from 'lucide-react';

import { UpgradeBadge } from '@/components/subscription/FeatureGate';
import type { FeatureGate } from '@/lib/featureGates';
import { cn } from '@/lib/utils';

import { DockStepButton } from './DockHeaderParts';
import { DOCK_CONTROL } from './dockStyles';
import { rovingTarget } from './rovingFocus';

/**
 * The slice of `useFeature()`'s result a gated dock control needs. A dock can
 * pass the hook's result straight through, or build one for a control that
 * two gates guard (clearance's Clip to interval: the analysis AND the clip
 * box) — whichever is shut is the one to name.
 */
export interface DockGate {
  allowed: boolean;
  promptUpgrade: () => void;
  gate: FeatureGate;
}

// ----------------------------------------------------------------- toolbar

export interface DockToolbarProps {
  /** Names the group for assistive tech ("Chart tools"). Omit for a purely visual group. */
  ariaLabel?: string;
  testId?: string;
  className?: string;
  children: ReactNode;
}

/**
 * A row of dock tools. `role="group"` (with a label), not `role="toolbar"`:
 * a toolbar promises arrow-key navigation BETWEEN its controls, and these
 * contain composite widgets (a radiogroup, a stepper) that need the arrows
 * for themselves.
 */
export const DockToolbar = ({ ariaLabel, testId, className, children }: DockToolbarProps) => (
  <div
    role={ariaLabel ? 'group' : undefined}
    aria-label={ariaLabel}
    data-testid={testId}
    className={cn('flex min-w-0 shrink-0 items-center gap-1.5 text-xs', className)}
  >
    {children}
  </div>
);

/** The rule between tab-contextual tools and the dock-wide ones. */
// Hidden in a phone-width container, where the toolbars wrap and a divider
// would end up dangling at the end of a line.
export const DockToolbarSeparator = () => (
  <span aria-hidden className='mx-1 h-4 w-px shrink-0 bg-slate-300 dark:bg-stone-600 @max-[560px]:hidden' />
);

// -------------------------------------------------------------- icon button

export interface DockIconButtonProps extends Omit<ComponentProps<'button'>, 'disabled' | 'title'> {
  /** Accessible name AND tooltip — an icon-only button needs both. */
  label: string;
  /** Overrides the tooltip (e.g. to add the hotkey) without changing the name. */
  title?: string;
  /** A toggle's state, styled by `DOCK_CONTROL.iconButton`'s `aria-pressed:`. */
  pressed?: boolean;
  /** A data condition: inert, `aria-disabled`, this as the tooltip. */
  disabledReason?: string | null;
  /** A paywall: a click prompts the upgrade instead of acting. */
  gate?: DockGate;
  testId?: string;
}

/**
 * A size-7 icon button. Spreads the rest of the button props, so it can be a
 * Radix trigger (`<PopoverTrigger asChild><DockIconButton …/>`).
 */
export const DockIconButton = ({
  label,
  title,
  pressed,
  disabledReason,
  gate,
  testId,
  onClick,
  className,
  children,
  ...rest
}: DockIconButtonProps) => {
  const locked = gate ? !gate.allowed : false;
  const inert = locked || !!disabledReason;
  return (
    <button
      type='button'
      {...rest}
      data-testid={testId}
      aria-label={label}
      title={disabledReason ?? title ?? label}
      aria-pressed={pressed}
      aria-disabled={inert || undefined}
      data-locked={locked ? 'true' : undefined}
      onClick={(e) => {
        if (locked) {
          gate?.promptUpgrade();
          return;
        }
        if (disabledReason) return;
        onClick?.(e);
      }}
      className={cn(DOCK_CONTROL.iconButton, inert && DOCK_CONTROL.inert, className)}
    >
      {children}
    </button>
  );
};

// --------------------------------------------------------------- segmented

export interface DockSegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** What the option means — "End area" is not a volume. */
  title?: string;
  /** A data reason this option cannot be picked; arrows skip it. */
  disabledReason?: string | null;
  testId?: string;
}

export interface DockSegmentedProps<T extends string> {
  ariaLabel: string;
  options: DockSegmentedOption<T>[];
  /** The checked option. `null` checks nothing (the first option keeps the tab stop). */
  value: T | null;
  onChange: (value: T) => void;
  /** The whole group's paywall: every click and arrow prompts the upgrade. */
  gate?: DockGate;
  /** The group's tooltip. */
  title?: string;
  testId?: string;
  /**
   * `segmented` — a joined pill row for the tab bar (Count | Depth).
   * `list` — one option per line with a radio dot, for `DockViewMenu`
   * (`DockMenuRadioGroup` is this with `list`).
   */
  variant?: 'segmented' | 'list';
  className?: string;
}

/**
 * A single-choice control as a proper ARIA radiogroup (APG "Radio Group"):
 * ONE tab stop — the checked option — and the arrows move the check AND the
 * focus together, skipping options that are unavailable for a data reason.
 * Replaces the docks' hand-rolled Count|Depth, render-mode and SV metric
 * toggles, which had drifted to three keyboard behaviours.
 */
export const DockSegmented = <T extends string>({
  ariaLabel,
  options,
  value,
  onChange,
  gate,
  title,
  testId,
  variant = 'segmented',
  className,
}: DockSegmentedProps<T>) => {
  const optionRefs = useRef(new Map<string, HTMLButtonElement>());
  const locked = gate ? !gate.allowed : false;
  const checkedIndex = options.findIndex((o) => o.value === value);
  const stopIndex = checkedIndex >= 0 ? checkedIndex : Math.max(0, options.findIndex((o) => !o.disabledReason));

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const at = checkedIndex >= 0 ? checkedIndex : stopIndex;
    const next = rovingTarget(
      e.key,
      at,
      options.length,
      variant === 'list' ? 'vertical' : 'both',
      (i) => !options[i].disabledReason,
    );
    if (next === null) return;
    e.preventDefault();
    // The paywall answers first: it is the one unavailable state with
    // somewhere for the key to go.
    if (locked) {
      gate?.promptUpgrade();
      return;
    }
    if (next === at) return;
    onChange(options[next].value);
    optionRefs.current.get(options[next].value)?.focus();
  };

  return (
    <div
      role='radiogroup'
      aria-label={ariaLabel}
      aria-disabled={locked || undefined}
      data-locked={locked ? 'true' : undefined}
      data-testid={testId}
      title={title}
      onKeyDown={onKeyDown}
      className={cn(
        variant === 'segmented'
          ? 'flex h-7 shrink-0 items-stretch overflow-hidden rounded-md border border-slate-300 dark:border-stone-600'
          : 'flex flex-col',
        className,
      )}
    >
      {options.map((o, i) => {
        const checked = o.value === value;
        const inert = locked || !!o.disabledReason;
        return (
          <button
            key={o.value}
            ref={(el) => {
              if (el) optionRefs.current.set(o.value, el);
              else optionRefs.current.delete(o.value);
            }}
            type='button'
            role='radio'
            data-testid={o.testId}
            aria-checked={checked}
            aria-disabled={inert || undefined}
            data-locked={locked ? 'true' : undefined}
            data-unavailable={o.disabledReason ? 'true' : undefined}
            tabIndex={i === stopIndex ? 0 : -1}
            title={o.disabledReason ?? o.title}
            onClick={() => {
              if (locked) {
                gate?.promptUpgrade();
                return;
              }
              if (o.disabledReason) return;
              onChange(o.value);
            }}
            className={cn(
              'focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none focus-visible:ring-inset',
              variant === 'segmented'
                ? cn(
                    'flex items-center px-2 whitespace-nowrap not-first:border-l not-first:border-slate-300 dark:not-first:border-stone-600',
                    checked
                      ? 'bg-slate-200 font-semibold text-slate-900 dark:bg-stone-700 dark:text-white'
                      : 'text-slate-500 hover:bg-slate-100 dark:text-stone-400 dark:hover:bg-stone-800',
                  )
                : cn(
                    'flex min-h-7 items-center gap-2 rounded px-2 text-left hover:bg-slate-100 dark:hover:bg-stone-800',
                    checked ? 'font-medium text-slate-900 dark:text-white' : 'text-slate-600 dark:text-stone-300',
                  ),
              inert && DOCK_CONTROL.inert,
            )}
          >
            {variant === 'list' && (
              <span
                aria-hidden
                className={cn(
                  'flex size-3.5 shrink-0 items-center justify-center rounded-full border',
                  checked ? 'border-sky-600 dark:border-sky-400' : 'border-slate-400 dark:border-stone-500',
                )}
              >
                {checked && <span className='size-1.5 rounded-full bg-sky-600 dark:bg-sky-400' />}
              </span>
            )}
            {o.label}
          </button>
        );
      })}
      {locked && gate && (
        <span className={cn('flex items-center', variant === 'segmented' ? 'px-1.5' : 'px-2 py-1')}>
          <UpgradeBadge gate={gate.gate} />
        </span>
      )}
    </div>
  );
};

// ----------------------------------------------------------------- stepper

export interface DockStepperSide {
  /** The button's accessible name — the action, or why it is inert. */
  label: string;
  /** Hover text, when it should differ from `label` (e.g. with the hotkey). */
  title?: string;
  /** A data condition — nothing that way. `aria-disabled`, click swallowed. */
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
}

export interface DockStepperCenter {
  /** The middle's text: "Go to worst", or a position ("12+100 – 12+120"). */
  label: ReactNode;
  /** The icon that replaces `label` in compact mode. Defaults to a crosshair. */
  icon?: ReactNode;
  /** The accessible name, required when compact hides a non-string label. */
  ariaLabel?: string;
  title?: string;
  /** Present: the middle is a BUTTON (Go to worst). Absent: a read-out. */
  onClick?: () => void;
  disabled?: boolean;
  testId?: string;
  /** Render the label in the station mono face. */
  mono?: boolean;
}

export interface DockStepperProps {
  /** The group's name ("Go to worst violation"). */
  ariaLabel: string;
  prev: DockStepperSide;
  next: DockStepperSide;
  center: DockStepperCenter;
  /**
   * `'auto'` shrinks the middle to its icon when the enclosing `@container`
   * — the tab bar — is under 1000 px (spec §B1); `true` always, `false`
   * never. Default: `'auto'` for a button middle (Go to worst), `false` for a
   * read-out (a section position has no icon to shrink to). The text stays in
   * the accessibility tree either way (`sr-only`, not `hidden`).
   */
  compact?: boolean | 'auto';
  /** A paywall over the whole stepper. Navigation is normally NOT gated — see brief decision 4. */
  gate?: DockGate;
  testId?: string;
}

/**
 * ◀ label ▶ — ONE stepper for violation stepping in the tab bar and the
 * section interval stepper, built on `DockStepButton` so it is the same glyph
 * buttons the sections had. The three parts are joined (shared borders,
 * squared inner corners).
 */
export const DockStepper = ({ ariaLabel, prev, next, center, compact: compactProp, gate, testId }: DockStepperProps) => {
  const compact = compactProp ?? (center.onClick ? 'auto' : false);
  const locked = gate ? !gate.allowed : false;
  const guard = (fn: () => void) => () => {
    if (locked) {
      gate?.promptUpgrade();
      return;
    }
    fn();
  };
  const iconNode = center.icon ?? <Crosshair className={DOCK_CONTROL.iconSm} aria-hidden />;
  // Compact hides the text and shows the icon; 'auto' does it by container
  // query, so the switch costs no measurement and no re-render.
  const labelCls = compact === true ? 'sr-only' : compact === 'auto' ? '@max-[1000px]:sr-only' : '';
  const iconCls = compact === true ? '' : compact === 'auto' ? 'hidden @max-[1000px]:inline-flex' : 'hidden';
  const middleInert = locked || !!center.disabled;
  const middleClass = cn(
    'flex h-7 shrink-0 items-center gap-1 border-y border-slate-300 px-2 whitespace-nowrap dark:border-stone-600',
    center.mono && 'sv-sta',
  );
  const middleBody = (
    <>
      <span className={cn('inline-flex', iconCls)}>{iconNode}</span>
      <span className={labelCls}>{center.label}</span>
    </>
  );
  return (
    <div
      role='group'
      aria-label={ariaLabel}
      data-testid={testId}
      data-locked={locked ? 'true' : undefined}
      className='flex shrink-0 items-center'
    >
      <DockStepButton
        dir={-1}
        testId={prev.testId}
        label={prev.label}
        title={prev.title}
        disabled={locked ? false : prev.disabled}
        onClick={guard(prev.onClick)}
        className={cn('rounded-r-none', locked && DOCK_CONTROL.inert)}
      />
      {center.onClick ? (
        <button
          type='button'
          data-testid={center.testId}
          aria-label={center.ariaLabel}
          title={center.title ?? center.ariaLabel}
          aria-disabled={middleInert || undefined}
          onClick={() => {
            if (locked) {
              gate?.promptUpgrade();
              return;
            }
            if (center.disabled) return;
            center.onClick?.();
          }}
          className={cn(
            middleClass,
            'hover:bg-slate-200/70 focus-visible:relative focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:hover:bg-stone-700/70',
            middleInert && cn(DOCK_CONTROL.inert, 'hover:bg-transparent dark:hover:bg-transparent'),
          )}
        >
          {middleBody}
        </button>
      ) : (
        <span
          data-testid={center.testId}
          title={center.title}
          aria-live='polite'
          className={cn(middleClass, 'text-slate-700 dark:text-stone-200')}
        >
          {middleBody}
        </span>
      )}
      <DockStepButton
        dir={1}
        testId={next.testId}
        label={next.label}
        title={next.title}
        disabled={locked ? false : next.disabled}
        onClick={guard(next.onClick)}
        className={cn('rounded-l-none', locked && DOCK_CONTROL.inert)}
      />
      {locked && gate && <UpgradeBadge gate={gate.gate} className='ml-1.5' />}
    </div>
  );
};
